"use strict";
import { getFewTokenFromOriginalToken } from "@ring-protocol/few-v2-sdk";
import { Token } from "@uniswap/sdk-core";
import { ZERO_ADDRESS } from "constants/misc";
import { isAddress } from "utilities/src/addresses";
function asNonEmptyString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : void 0;
}
function looksLikeLpTokenInfo(info) {
  const symbol = info.symbol.trim().toLowerCase();
  const name = info.name.trim().toLowerCase();
  const tags = info.tags?.map((tag) => tag.toLowerCase()) ?? [];
  return tags.some((tag) => tag === "lp-token" || tag === "liquidity-token") || symbol === "ring-v2" || symbol === "uni-v2" || symbol.endsWith("-lp") || name.includes(" lp token") || name.includes("liquidity pool token") || name === "ring v2";
}
export function getFewTokenMappingContractAddress(token, selectionMode) {
  if (selectionMode === "few-token") {
    return token.address;
  }
  try {
    const fewToken = getFewTokenFromOriginalToken(token, token.chainId);
    return fewToken.address.toLowerCase() === token.address.toLowerCase() ? void 0 : fewToken.address;
  } catch {
    return void 0;
  }
}
export function isVerifiedImportedFewTokenMapping({
  selectedToken,
  resolvedOriginAddress,
  factoryFewTokenAddress,
  originPairFactoryAddress,
  selectionMode
}) {
  const originAddress = isAddress(resolvedOriginAddress);
  const canonicalFewTokenAddress = isAddress(factoryFewTokenAddress);
  const originPairFactory = isAddress(originPairFactoryAddress);
  if (!originAddress || !canonicalFewTokenAddress) {
    return false;
  }
  if (originPairFactory && originPairFactory.toLowerCase() !== ZERO_ADDRESS) {
    return false;
  }
  if (selectionMode === "origin-token") {
    const expectedFewToken = getFewTokenMappingContractAddress(selectedToken, selectionMode);
    return selectedToken.address.toLowerCase() === originAddress.toLowerCase() && expectedFewToken?.toLowerCase() === canonicalFewTokenAddress.toLowerCase();
  }
  try {
    const originToken = new Token(selectedToken.chainId, originAddress, selectedToken.decimals, void 0, void 0);
    const expectedFewTokenAddress = getFewTokenFromOriginalToken(
      originToken,
      selectedToken.chainId
    ).address.toLowerCase();
    return expectedFewTokenAddress === selectedToken.address.toLowerCase() && canonicalFewTokenAddress.toLowerCase() === selectedToken.address.toLowerCase();
  } catch {
    return false;
  }
}
export function getVerifiedFewTokenMappingInfo(info) {
  const originAddress = isAddress(info.address);
  const extensions = info.extensions;
  const listedFewTokenAddress = isAddress(asNonEmptyString(extensions?.fewWrappedAddress));
  if (!originAddress || !listedFewTokenAddress || looksLikeLpTokenInfo(info) || originAddress.toLowerCase() === listedFewTokenAddress.toLowerCase()) {
    return void 0;
  }
  try {
    const originToken = new Token(info.chainId, originAddress, info.decimals, info.symbol, info.name);
    const expectedFewToken = getFewTokenFromOriginalToken(originToken, info.chainId);
    if (expectedFewToken.address.toLowerCase() === originAddress.toLowerCase() || expectedFewToken.address.toLowerCase() !== listedFewTokenAddress.toLowerCase()) {
      return void 0;
    }
    return {
      originTokenInfo: { ...info, address: originAddress },
      fewTokenInfo: {
        ...info,
        address: listedFewTokenAddress,
        name: asNonEmptyString(extensions?.fewName) ?? `Few Wrapped ${info.name}`,
        symbol: asNonEmptyString(extensions?.fewSymbol) ?? `fw${info.symbol}`,
        extensions: void 0
      }
    };
  } catch {
    return void 0;
  }
}
export function getVerifiedFewTokenMappingInfos(infos, chainIds) {
  const allowedChains = chainIds?.length ? new Set(chainIds) : void 0;
  const seenFewTokens = /* @__PURE__ */ new Set();
  const mappings = [];
  for (const info of infos) {
    if (allowedChains && !allowedChains.has(info.chainId)) {
      continue;
    }
    const mapping = getVerifiedFewTokenMappingInfo(info);
    if (!mapping) {
      continue;
    }
    const key = `${mapping.fewTokenInfo.chainId}:${mapping.fewTokenInfo.address.toLowerCase()}`;
    if (seenFewTokens.has(key)) {
      continue;
    }
    seenFewTokens.add(key);
    mappings.push(mapping);
  }
  return mappings;
}
